import { RefObject, useEffect } from 'react';
import { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';

export type TouchPair = { distance: number; midX: number; midY: number };
export type ZoomTransform = { x: number; y: number; scale: number };
type Gesture = { distance: number; contentX: number; contentY: number };

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// Remember the content point under the fingers so it stays under them
export function startGesture(
  pair: TouchPair,
  { x, y, scale }: ZoomTransform
): Gesture {
  return {
    distance: pair.distance / scale,
    contentX: (pair.midX - x) / scale,
    contentY: (pair.midY - y) / scale,
  };
}

// Scale follows the finger spread; position keeps the content inside the box
export function getGestureTransform(
  gesture: Gesture,
  pair: TouchPair,
  width: number,
  height: number,
  maxScale: number
): ZoomTransform {
  const scale = clamp(pair.distance / gesture.distance, 1, maxScale);
  return {
    scale,
    x: clamp(pair.midX - gesture.contentX * scale, width - width * scale, 0),
    y: clamp(pair.midY - gesture.contentY * scale, height - height * scale, 0),
  };
}

function getTouchPair(event: TouchEvent, box: HTMLElement): TouchPair {
  const [a, b] = [event.touches[0], event.touches[1]];
  const rect = box.getBoundingClientRect();
  return {
    distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
    midX: (a.clientX + b.clientX) / 2 - rect.left,
    midY: (a.clientY + b.clientY) / 2 - rect.top,
  };
}

// Touch on a react-zoom-pan-pinch view: one finger always scrolls the page,
// two fingers zoom and move the content (the library can't pan with two).
// Give its TransformWrapper `pinch={{ disabled: true }}` and the target `touch-action: pan-y`.
export function attachTwoFingerZoom(
  target: HTMLElement,
  zoomRef: RefObject<ReactZoomPanPinchRef | null>,
  maxScale: number
): () => void {
  let gesture: Gesture | null = null;

  function disableTouchPanning() {
    const zoom = zoomRef.current;
    if (zoom) zoom.instance.setup.panning.disabled = true;
  }

  // Touches landing on the same point give no spread to scale from; wait for them to part
  function tryStartGesture(event: TouchEvent) {
    const instance = zoomRef.current?.instance;
    if (event.touches.length !== 2 || !instance?.wrapperComponent) return;
    const pair = getTouchPair(event, instance.wrapperComponent);
    if (pair.distance <= 0) return;
    const { scale, positionX, positionY } = instance.transformState;
    gesture = startGesture(pair, { x: positionX, y: positionY, scale });
  }

  function handleTouchMove(event: TouchEvent) {
    if (!gesture) tryStartGesture(event);
    const zoom = zoomRef.current;
    const wrapper = zoom?.instance.wrapperComponent;
    if (!gesture || event.touches.length !== 2 || !zoom || !wrapper) return;
    event.preventDefault();
    const { x, y, scale } = getGestureTransform(
      gesture,
      getTouchPair(event, wrapper),
      wrapper.clientWidth,
      wrapper.clientHeight,
      maxScale
    );
    zoom.setTransform(x, y, scale, 0);
  }

  // Any lift changes the pair (e.g. 3 → 2 fingers); the next move starts afresh
  function handleTouchEnd() {
    gesture = null;
  }

  const capture = { capture: true, passive: true };
  target.addEventListener('touchstart', disableTouchPanning, capture);
  target.addEventListener('touchstart', tryStartGesture, { passive: true });
  target.addEventListener('touchmove', handleTouchMove, { passive: false });
  target.addEventListener('touchend', handleTouchEnd);
  target.addEventListener('touchcancel', handleTouchEnd);
  return () => {
    target.removeEventListener('touchstart', disableTouchPanning, capture);
    target.removeEventListener('touchstart', tryStartGesture);
    target.removeEventListener('touchmove', handleTouchMove);
    target.removeEventListener('touchend', handleTouchEnd);
    target.removeEventListener('touchcancel', handleTouchEnd);
  };
}

export function useTwoFingerZoom(
  targetRef: RefObject<HTMLElement | null>,
  zoomRef: RefObject<ReactZoomPanPinchRef | null>,
  maxScale: number
) {
  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;
    return attachTwoFingerZoom(target, zoomRef, maxScale);
  }, [targetRef, zoomRef, maxScale]);
}
